import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../dashboard/components/AdminLayout';
import { useAdminCompanies } from '../hooks/useAdminCompanies';
import { useSectors } from '../hooks/useSectors';
import { useQualifications } from '../hooks/useQualifications';
import { CreateCompanyDrawer } from '../components/CreateCompanyDrawer';
import { DeleteConfirmationDialog } from '../components/DeleteConfirmationDialog';
import { AddSectorModal } from '../components/AddSectorModal';
import { AddQualificationModal } from '../components/AddQualificationModal';
import { ManageListsModal } from '../components/ManageListsModal';
import { AdminCompany } from '../types';
import { COLORS } from '../../../../core/constants/colors';

type ViewMode = 'table' | 'board' | 'list';
type TabType = 'all' | 'african' | 'non-african';

export const AdminCompaniesPage: React.FC = () => {
  const navigate = useNavigate();
  const { companies, isLoading, createCompany, deleteCompany } = useAdminCompanies();
  // Single source of truth — fetch all (active + inactive), derive active client-side.
  const { sectors: allSectors, createSector, updateSector, deleteSector } = useSectors(false);
  const sectors = allSectors.filter((s) => s.is_active);

  const {
    qualifications: allQualifications,
    error: qualificationsError,
    createQualification,
    updateQualification,
    deleteQualification,
  } = useQualifications(false);
  const qualifications = allQualifications.filter((q) => q.is_active);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterSectorId, setFilterSectorId] = useState<string>('all');
  const [filterQualificationId, setFilterQualificationId] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddSectorModal, setShowAddSectorModal] = useState(false);
  const [showAddQualificationModal, setShowAddQualificationModal] = useState(false);
  const [showManageListsModal, setShowManageListsModal] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; company: AdminCompany | null }>({
    isOpen: false,
    company: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const itemsPerPage = 10;

  const africanCountries = ['Nigeria', 'Kenya', 'South Africa', 'Egypt', 'Ghana', 'Morocco', 'Tanzania', 'Uganda', 'Algeria', 'Tunisia', 'Ethiopia', 'Senegal', 'Cameroon', 'Ivory Coast', 'Rwanda', 'Zambia'];

  const getCompanyInitial = (name: string) => {
    return name?.charAt(0).toUpperCase() || 'C';
  };

  const getInitialColor = (name: string) => {
    const colors = ['#713FFF', '#FF5E2E', '#0E81FF', '#8CE58C', '#CFBEFF'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  const getStatusBadge = (isActive: boolean, index: number) => {
    const statuses = ['Active', 'Prospect', 'Inactive'];
    const status = isActive ? statuses[index % 3] : 'Inactive';

    const statusColors: Record<string, { bg: string; text: string }> = {
      Active: { bg: '#D1FAE5', text: '#059669' },
      Prospect: { bg: '#FEF3C7', text: '#D97706' },
      Inactive: { bg: '#F3F4F6', text: '#6B7280' },
    };

    return { status, ...statusColors[status] };
  };

  const getPrivatePublicBadge = (isPrivate: boolean) => {
    return {
      status: isPrivate ? 'Private' : 'Public',
      bg: isPrivate ? '#F3F4F6' : '#D1FAE5',
      text: isPrivate ? '#6B7280' : '#059669',
    };
  };

  const filteredCompanies = companies.filter((company) => {
    const matchesSearch = company.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.industry?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         company.location?.toLowerCase().includes(searchQuery.toLowerCase());

    const isAfrican = africanCountries.some(country =>
      company.location?.toLowerCase().includes(country.toLowerCase())
    );

    const matchesTab = activeTab === 'all' ? true :
                      activeTab === 'african' ? isAfrican :
                      !isAfrican;

    const matchesStatus = filterStatus === 'all' ? true :
                         filterStatus === 'active' ? company.is_active :
                         !company.is_active;

    const matchesSector = filterSectorId === 'all' ? true :
                          filterSectorId === 'unassigned' ? !company.sector_id :
                          company.sector_id === Number(filterSectorId);

    const matchesQualification = filterQualificationId === 'all' ? true :
                                 filterQualificationId === 'unassigned' ? !company.qualification_id :
                                 company.qualification_id === Number(filterQualificationId);

    return matchesSearch && matchesTab && matchesStatus && matchesSector && matchesQualification;
  });

  const paginatedCompanies = filteredCompanies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);


  const handleDeleteClick = (company: AdminCompany, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialog({ isOpen: true, company });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.company) return;

    setIsDeleting(true);
    try {
      await deleteCompany(deleteDialog.company.id);
      setDeleteDialog({ isOpen: false, company: null });
    } catch (error) {
      console.error('Error deleting company:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, company: null });
  };

  if (isLoading) {
    return (
      <AdminLayout pageTitle="Company Management" pageDescription="Manage and oversee all portfolio companies">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.admin.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: '#6B7280' }}>Loading companies...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Company Management" pageDescription="Manage and oversee all portfolio companies">
      <div style={{ backgroundColor: '#F5F5F5' }}>
      <div className="max-w-[1400px] mx-auto px-8 py-8">
          {/* Action Bar */}
        <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white shadow-sm' : ''}`}
                style={{ color: viewMode === 'table' ? '#1F2937' : '#6B7280' }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                </svg>
                Table
              </button>
              <button
                onClick={() => setViewMode('board')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'board' ? 'bg-white shadow-sm' : ''}`}
                style={{ color: viewMode === 'board' ? '#1F2937' : '#6B7280' }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
                Board
              </button>
          <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                style={{ color: viewMode === 'list' ? '#1F2937' : '#6B7280' }}
          >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
                List
          </button>
        </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <svg className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                  placeholder="Search Companies"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: '#E5E7EB' }}
              />
            </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <select
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value as TabType)}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              >
                <option value="all">All Countries</option>
                <option value="african">African</option>
                <option value="non-african">Non African</option>
              </select>
              <select
                value={filterSectorId}
                onChange={(e) => setFilterSectorId(e.target.value)}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              >
                <option value="all">All Sectors</option>
                <option value="unassigned">Unassigned</option>
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.id}>{sector.name}</option>
                ))}
              </select>
              <select
                value={filterQualificationId}
                onChange={(e) => setFilterQualificationId(e.target.value)}
                className="px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: '#E5E7EB' }}
              >
                <option value="all">All Qualifications</option>
                <option value="unassigned">Unassigned</option>
                {qualifications.map((q) => (
                  <option key={q.id} value={q.id}>{q.name}</option>
                ))}
              </select>
              {qualificationsError && (
                <span className="text-xs text-red-500 self-center">Could not load qualifications</span>
              )}
              <button
                onClick={() => setShowAddSectorModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:bg-gray-50"
                style={{ border: `1px solid ${COLORS.admin.primary}`, color: COLORS.admin.primary }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Sector
              </button>
              <button
                onClick={() => setShowAddQualificationModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:bg-gray-50"
                style={{ border: `1px solid ${COLORS.admin.primary}`, color: COLORS.admin.primary }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Relationship
              </button>
              <button
                onClick={() => setShowManageListsModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all hover:bg-gray-50"
                style={{ border: '1px solid #E5E7EB', color: '#6B7280' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Manage Lists
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-all hover:shadow-md"
                style={{ backgroundColor: COLORS.admin.primary }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Company
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-8 mb-6 border-b" style={{ borderColor: '#E5E7EB' }}>
            <button
              onClick={() => setActiveTab('all')}
              className={`pb-3 font-medium transition-all ${activeTab === 'all' ? 'border-b-2' : ''}`}
              style={{
                color: activeTab === 'all' ? COLORS.admin.primary : '#6B7280',
                borderColor: activeTab === 'all' ? COLORS.admin.primary : 'transparent'
              }}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('african')}
              className={`pb-3 font-medium transition-all ${activeTab === 'african' ? 'border-b-2' : ''}`}
              style={{
                color: activeTab === 'african' ? COLORS.admin.primary : '#6B7280',
                borderColor: activeTab === 'african' ? COLORS.admin.primary : 'transparent'
              }}
            >
              African
            </button>
            <button
              onClick={() => setActiveTab('non-african')}
              className={`pb-3 font-medium transition-all ${activeTab === 'non-african' ? 'border-b-2' : ''}`}
              style={{
                color: activeTab === 'non-african' ? COLORS.admin.primary : '#6B7280',
                borderColor: activeTab === 'non-african' ? COLORS.admin.primary : 'transparent'
              }}
            >
              Non African
            </button>
        </div>

          {/* Table View */}
          {viewMode === 'table' && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
                <thead style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                      Company
                  </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                      Type
                  </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                      Sector
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                      Qualification
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Private/Public
                  </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold" style={{ color: '#6B7280' }}>
                      Last Activity
                  </th>
                    <th className="px-6 py-3 text-right text-sm font-semibold" style={{ color: '#6B7280' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedCompanies.length > 0 ? (
                    paginatedCompanies.map((company, index) => {
                      const privatePublicBadge = getPrivatePublicBadge(company.is_private ?? false);

                      return (
                    <tr
                      key={company.id}
                      className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
                          style={{ borderColor: '#E5E7EB' }}
                      onClick={() => navigate(`/admin/companies/${company.id}`)}
                    >
                      <td className="px-6 py-4">
                          <div>
                              <p className="font-medium text-sm" style={{ color: '#1F2937' }}>
                              {company.name}
                            </p>
                              <p className="text-xs" style={{ color: '#9CA3AF' }}>
                                {company.website || 'No website'}
                            </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                            <span className="text-sm" style={{ color: '#1F2937' }}>
                              {company.industry || 'Technology'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {company.sector_name ? (
                          <span
                            className="px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }}
                          >
                            {company.sector_name}
                          </span>
                        ) : (
                          <span className="text-sm" style={{ color: '#9CA3AF' }}>Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {company.qualification_name ? (
                          <span
                            className="px-2.5 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: '#F0FDF4', color: '#166534' }}
                          >
                            {company.qualification_name}
                          </span>
                        ) : (
                          <span className="text-sm" style={{ color: '#9CA3AF' }}>Unassigned</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-medium"
                              style={{ backgroundColor: privatePublicBadge.bg, color: privatePublicBadge.text }}
                            >
                              {privatePublicBadge.status}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-sm" style={{ color: '#1F2937' }}>
                              {company.location || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                            <span className="text-sm" style={{ color: '#6B7280' }}>
                          {new Date(company.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/companies/${company.id}`);
                            }}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                                title="View"
                          >
                                <svg className="w-5 h-5" style={{ color: '#6B7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          <button
                                onClick={(e) => handleDeleteClick(company, e)}
                                className="p-2 rounded-lg hover:bg-gray-100 transition-all"
                            title="Delete"
                          >
                                <svg className="w-5 h-5" style={{ color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                      );
                    })
                ) : (
                  <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <p style={{ color: '#6B7280' }}>No companies found</p>
                      </td>
                    </tr>
                )}
              </tbody>
            </table>
          </div>
          )}

          {/* Board View */}
          {viewMode === 'board' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {paginatedCompanies.map((company, index) => {
                const statusBadge = getStatusBadge(company.is_active, index);

                return (
                  <div
                    key={company.id}
                    onClick={() => navigate(`/admin/companies/${company.id}`)}
                    className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all cursor-pointer border"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold"
                        style={{ backgroundColor: getInitialColor(company.name) }}
                      >
                        {getCompanyInitial(company.name)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg" style={{ color: '#1F2937' }}>
                          {company.name}
                        </h3>
                        <p className="text-sm" style={{ color: '#6B7280' }}>{company.industry || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>Location:</span>
                        <span className="font-medium" style={{ color: '#1F2937' }}>{company.location || 'N/A'}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>Sector:</span>
                        <span className="font-medium" style={{ color: company.sector_name ? '#1F2937' : '#9CA3AF' }}>
                          {company.sector_name || 'Unassigned'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>Qualification:</span>
                        <span className="font-medium" style={{ color: company.qualification_name ? '#166534' : '#9CA3AF' }}>
                          {company.qualification_name || 'Unassigned'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span style={{ color: '#6B7280' }}>Status:</span>
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{ backgroundColor: statusBadge.bg, color: statusBadge.text }}
                        >
                          {statusBadge.status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* List View */}
          {viewMode === 'list' && (
            <div className="bg-white rounded-lg shadow-sm">
              {paginatedCompanies.map((company, index) => {
                const statusBadge = getStatusBadge(company.is_active, index);

                return (
                  <div
                    key={company.id}
                    onClick={() => navigate(`/admin/companies/${company.id}`)}
                    className="flex items-center gap-6 p-6 border-b hover:bg-gray-50 cursor-pointer transition-colors"
                    style={{ borderColor: '#E5E7EB' }}
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold flex-shrink-0"
                      style={{ backgroundColor: getInitialColor(company.name) }}
                    >
                      {getCompanyInitial(company.name)}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-1" style={{ color: '#1F2937' }}>
                        {company.name}
                      </h3>
                      <p className="text-sm" style={{ color: '#6B7280' }}>
                        {company.industry || 'N/A'} • {company.location || 'N/A'}
                        {company.sector_name && <> • <span style={{ color: '#4338CA' }}>{company.sector_name}</span></>}
                        {company.qualification_name && <> • <span style={{ color: '#166534' }}>{company.qualification_name}</span></>}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: statusBadge.bg, color: statusBadge.text }}
                      >
                        {statusBadge.status}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: '#E5E7EB' }}
              >
                ← Previous
              </button>

              {[...Array(Math.min(10, totalPages))].map((_, i) => {
                const pageNum = i + 1;
                if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-10 h-10 rounded-lg font-medium ${currentPage === pageNum ? 'text-white' : 'hover:bg-gray-50'}`}
                      style={{
                        backgroundColor: currentPage === pageNum ? COLORS.admin.primary : 'transparent',
                        color: currentPage === pageNum ? '#FFFFFF' : '#6B7280'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                  return <span key={pageNum} className="px-2">...</span>;
                }
                return null;
              })}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: '#E5E7EB' }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
        </div>

      {/* Create Company Drawer */}
        {showCreateModal && (
        <CreateCompanyDrawer
            onClose={() => setShowCreateModal(false)}
            sectors={sectors}
            onCreateSector={createSector}
            qualifications={qualifications}
          onSubmit={async (data) => {
            const payload = {
              name: data.name,
              domain: data.domain,
              description: data.description || undefined,
              website: data.website || undefined,
              founding_year: data.founding_year || undefined,
              employees: data.employees || undefined,
              industry: data.industry,
              location: data.location,
              linkedin_url: data.linkedin_url || '',
              auto_enrich: data.auto_enrich ?? true,
              sector_id: data.sector_id ?? null,
              qualification_id: data.qualification_id ?? null,
            };
            await createCompany(payload);
            setShowCreateModal(false);
          }}
          />
        )}

      {/* Add Sector Modal */}
      <AddSectorModal
        isOpen={showAddSectorModal}
        onClose={() => setShowAddSectorModal(false)}
        onSubmit={async (payload) => {
          await createSector(payload);
        }}
      />

      {/* Add Relationship Modal */}
      <AddQualificationModal
        isOpen={showAddQualificationModal}
        onClose={() => setShowAddQualificationModal(false)}
        onSubmit={async (payload) => {
          await createQualification(payload);
        }}
      />

      {/* Manage Lists Modal */}
      <ManageListsModal
        isOpen={showManageListsModal}
        onClose={() => setShowManageListsModal(false)}
        allSectors={allSectors}
        onUpdateSector={updateSector}
        onDeleteSector={deleteSector}
        allQualifications={allQualifications}
        onUpdateQualification={updateQualification}
        onDeleteQualification={deleteQualification}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        companyName={deleteDialog.company?.name || ''}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
      />
    </AdminLayout>
  );
};
